/**
 * JulietteSignalsList — Liste des signaux proactifs de Juliette, reutilisable.
 *
 * Rend visibles les signaux detectes par le cron (dormant, mandat expirant, lead
 * chaud bloque) avec 2 actions : "Voir le contact" (deep-link /crm/contact/:id)
 * et "Ecarter" (dismiss). Sert a la carte dashboard (variant "light", themee) ET
 * au rail de la surface Juliette (variant "dark", fond sombre fixe).
 *
 * La query partage la cle ["juliette-signals", teamId] : la carte qui decide de
 * s'afficher et cette liste tapent le meme cache (zero double requete).
 */

import { type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Moon, CalendarClock, Flame, Inbox, Route, Check, ChevronRight, X, CheckCircle2, Sparkles, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useLastExchange } from "@/hooks/use-last-exchange";
import { useRelanceDraft } from "@/hooks/use-relance-draft";
import { useEnrollInSequence } from "@/hooks/use-sequences";
import { relanceInstructionsForSignal, canPrepareRelance } from "@/lib/juliette-relance-instructions";
import { buildSignalContextLine } from "@/lib/services/signal-reco.service";
import { PendingEmailDialog } from "@/components/pending-email/PendingEmailDialog";
import {
  listJulietteSignals,
  dismissSignal,
  resolveSignal,
  markSignalSeen,
  signalTypeLabel,
  type JulietteSignal,
  type SignalSeverity,
} from "@/lib/juliette-signals";

type Variant = "light" | "dark";

function iconFor(type: string) {
  switch (type) {
    case "dormant":
      return Moon;
    case "mandate_expiring":
      return CalendarClock;
    case "hot_blocked":
      return Flame;
    case "to_triage":
      return Inbox;
    case "campaign_reco":
      return Route;
    default:
      return ChevronRight;
  }
}

/** Couleur d'accent par severite (memes teintes vives sur fond clair ou sombre). */
function severityAccent(sev: SignalSeverity): { dot: string; icon: string } {
  switch (sev) {
    case "urgent":
      return { dot: "bg-destructive", icon: "text-destructive" };
    case "warning":
      return { dot: "bg-warning", icon: "text-warning" };
    default:
      return { dot: "bg-sky-500", icon: "text-sky-500" };
  }
}

export function JulietteSignalsList({
  teamId,
  enabled,
  variant = "light",
  max,
  onNavigated,
}: {
  teamId: string | null;
  enabled: boolean;
  variant?: Variant;
  max?: number;
  /** Appele apres navigation vers un contact (ex : fermer la surface Juliette). */
  onNavigated?: () => void;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: signals = [], isLoading } = useQuery({
    queryKey: ["juliette-signals", teamId],
    enabled: enabled && !!teamId,
    queryFn: () => listJulietteSignals(),
    staleTime: 60_000,
  });

  // Dernier echange par contact (cache partage avec la table CRM) : alimente la
  // phrase « pourquoi le relancer » personnalisee de chaque signal.
  const { data: lastExchangeMap } = useLastExchange();

  const dismissMutation = useMutation({
    mutationFn: dismissSignal,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["juliette-signals", teamId] }),
    onError: (e: unknown) =>
      toast({ title: "Action impossible", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  // Pre-redaction de relance en 1 clic, via le flux partage (use-relance-draft).
  // Le dialog s'ouvre en loading pendant que generate-relance-email redige.
  const draft = useRelanceDraft();

  // Validation d'une reco campagne en 1 clic : inscrit le contact dans la
  // sequence conseillee (payload.sequence_id) via le hook existant, puis resout
  // le signal. « Elle propose, tu valides » (Phase 2 cycle de vie).
  const enroll = useEnrollInSequence();

  const prepareRelance = (s: JulietteSignal) => {
    const instructions = relanceInstructionsForSignal(s.signalType);
    if (!s.contactId || !instructions) return;
    void draft.startDraft({
      key: s.id,
      contactId: s.contactId,
      instructions,
      contactName: s.contactName ?? undefined,
      // Le signal a ete traite : on le marque vu (quitte la liste) sans bloquer l'UI.
      onDrafted: () => {
        void markSignalSeen(s.id)
          .then(() => queryClient.invalidateQueries({ queryKey: ["juliette-signals", teamId] }))
          .catch(() => {/* non bloquant : le brouillon est cree, le signal s'auto-resoudra */});
      },
    });
  };

  const recoSequenceId = (s: JulietteSignal): string | null =>
    s.signalType === "campaign_reco" && typeof s.payload?.sequence_id === "string"
      ? (s.payload.sequence_id as string)
      : null;

  const validateReco = (s: JulietteSignal) => {
    const seqId = recoSequenceId(s);
    if (!s.contactId || !seqId) return;
    enroll.mutate(
      { sequenceId: seqId, contactId: s.contactId },
      {
        onSuccess: () => {
          void resolveSignal(s.id)
            .then(() => queryClient.invalidateQueries({ queryKey: ["juliette-signals", teamId] }))
            .catch(() => {/* non bloquant : l'inscription est faite */});
        },
      },
    );
  };

  const goToContact = (s: JulietteSignal) => {
    navigate(s.contactId ? `/crm/contact/${s.contactId}` : "/crm");
    onNavigated?.();
  };

  const shown = typeof max === "number" ? signals.slice(0, max) : signals;
  const dark = variant === "dark";

  // Le PendingEmailDialog est monte dans TOUS les cas (meme liste vide) : marquer
  // un signal "vu" apres preparation vide la liste, mais l'editeur doit rester ouvert.
  const renderWithDialog = (content: ReactNode) => (
    <>
      {content}
      <PendingEmailDialog {...draft.dialogProps} />
    </>
  );

  if (isLoading) {
    return renderWithDialog(
      <p className={cn("px-2 py-4 text-sm", dark ? "text-white/40" : "text-muted-foreground")}>Chargement…</p>,
    );
  }

  if (signals.length === 0) {
    return renderWithDialog(
      <div className="flex flex-col items-center justify-center gap-2 py-8 text-center px-6">
        <CheckCircle2 className={cn("h-7 w-7", dark ? "text-success/70" : "text-success/70")} />
        <p className={cn("text-sm", dark ? "text-white/60" : "text-muted-foreground")}>Rien à signaler.</p>
        <p className={cn("text-xs", dark ? "text-white/35" : "text-muted-foreground/70")}>
          Juliette surveille tes contacts dormants, mandats et leads chauds.
        </p>
      </div>,
    );
  }

  return renderWithDialog(
    <div className="space-y-2">
      {shown.map((s) => {
        const Icon = iconFor(s.signalType);
        const accent = severityAccent(s.severity);
        // Phrase « pourquoi relancer » personnalisee (meme moteur que la fiche).
        const contextLine = buildSignalContextLine(
          s,
          s.contactId ? lastExchangeMap?.get(s.contactId) : undefined,
        );
        return (
          <div
            key={s.id}
            className={cn(
              "group relative rounded-xl border pl-3.5 pr-1.5 py-2.5 transition-colors",
              dark ? "border-white/10 bg-white/[0.05] hover:bg-white/[0.08]" : "border-border bg-card/60 hover:bg-muted/50",
            )}
          >
            <span className={cn("absolute left-0 top-2.5 bottom-2.5 w-1 rounded-full", accent.dot)} />
            <div className="flex items-start gap-2.5">
              <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", accent.icon)} />
              <button
                type="button"
                onClick={() => goToContact(s)}
                className="flex-1 min-w-0 text-left"
              >
                <span className={cn("block text-sm leading-snug", dark ? "text-white/90" : "text-foreground")}>
                  {s.title}
                </span>
                {contextLine && (
                  <span className={cn("mt-0.5 block text-xs leading-snug", dark ? "text-white/50" : "text-muted-foreground")}>
                    {contextLine}
                  </span>
                )}
                <span className={cn("mt-1 inline-flex items-center gap-1 text-2xs", dark ? "text-white/40" : "text-muted-foreground/80")}>
                  <span className={cn("rounded-full px-1.5 py-0.5", dark ? "bg-white/10 text-white/55" : "bg-muted text-foreground/60")}>
                    {signalTypeLabel(s.signalType)}
                  </span>
                  {s.contactName && <span>· {s.contactName}</span>}
                </span>
              </button>

              <div className="flex shrink-0 items-center gap-0.5">
                {recoSequenceId(s) && (
                  <button
                    type="button"
                    disabled={enroll.isPending}
                    onClick={() => validateReco(s)}
                    aria-label="Valider la campagne conseillée"
                    title="Valider — inscrire à la campagne conseillée"
                    className={cn(
                      "h-7 px-2 rounded-md flex items-center gap-1 text-2xs font-medium transition-colors disabled:opacity-50",
                      dark
                        ? "bg-indigo-500/80 text-white hover:bg-indigo-500"
                        : "bg-primary text-primary-foreground hover:bg-primary/90",
                    )}
                  >
                    {enroll.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    Valider
                  </button>
                )}
                {s.contactId && canPrepareRelance(s.signalType) && (
                  <button
                    type="button"
                    disabled={draft.busyKey !== null}
                    onClick={() => prepareRelance(s)}
                    aria-label="Préparer la relance"
                    title="Préparer la relance"
                    className={cn(
                      "h-7 w-7 rounded-md flex items-center justify-center transition-colors disabled:opacity-50",
                      dark
                        ? "text-indigo-300 hover:bg-white/10 hover:text-indigo-200"
                        : "text-indigo-500 hover:bg-indigo-500/10 hover:text-indigo-600",
                    )}
                  >
                    {draft.busyKey === s.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => goToContact(s)}
                  aria-label="Voir le contact"
                  title="Voir le contact"
                  className={cn(
                    "h-7 w-7 rounded-md flex items-center justify-center transition-colors",
                    dark ? "text-white/50 hover:bg-white/10 hover:text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={dismissMutation.isPending}
                  onClick={() => dismissMutation.mutate(s.id)}
                  aria-label="Écarter"
                  title="Écarter ce signal"
                  className={cn(
                    "h-7 w-7 rounded-md flex items-center justify-center transition-colors disabled:opacity-40",
                    dark ? "text-white/35 hover:bg-white/10 hover:text-white/70" : "text-muted-foreground/60 hover:bg-muted hover:text-foreground",
                  )}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {typeof max === "number" && signals.length > max && (
        <p className={cn("px-1 pt-1 text-2xs", dark ? "text-white/40" : "text-muted-foreground")}>
          +{signals.length - max} autre{signals.length - max > 1 ? "s" : ""} signal
          {signals.length - max > 1 ? "aux" : ""}…
        </p>
      )}
    </div>
  );
}
